import { useLocation } from 'react-router-dom';
import SmartLink from '@/components/SmartLink';
import { useTracking } from '@mygpt/app/utils';

interface DropdownItem {
  title: string;
  subtitle?: string;
  to?: string;
  href?: string;
  target?: string;
  toTop?: boolean; // 是否滚动到页面顶部
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  icon?: string; // Material Symbols Rounded icon name(用于多列分组下拉)
  iconColor?: string; // icon 颜色 css(默认 #6B7280 灰)
}

/** Rounded(填充版)Material Symbols icon — 字体在 index.html 全局加载 */
const NavIcon: React.FC<{ name: string; color?: string }> = ({ name, color = '#8B8AA8' }) => (
  <span
    style={{
      fontFamily: '"Material Symbols Rounded"',
      fontVariationSettings: '"FILL" 1, "wght" 500, "GRAD" 0, "opsz" 24',
      fontSize: 22,
      lineHeight: 1,
      color,
      flexShrink: 0,
      marginTop: 2,
    }}
  >
    {name}
  </span>
);

interface DropdownGroup {
  label: string;
  items: DropdownItem[];
}

interface NavItem {
  title: string;
  type: 'link' | 'dropdown';
  to?: string;
  href?: string;
  items?: DropdownItem[];
  groups?: DropdownGroup[]; // 多列分组下拉
  toTop?: boolean; // 是否滚动到页面顶部
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * 导航菜单配置
 * 基于用户需求,整合Solution和官网的导航项
 */
const NAV_ITEMS: NavItem[] = [
  {
    title: 'ソリューション',
    type: 'dropdown',
    items: [
      { title: '商業施設・百貨店', to: '/solution#case-study' },
      // { title: 'ホテル・旅館', to: '/solution#core-features' },
      // { title: 'EC・オンラインストア', to: '/solution#core-features' },
    ],
  },
  {
    title: '製品／機能',
    type: 'dropdown',
    groups: [
      {
        label: '製品',
        items: [
          {
            title: 'GBase Concierge',
            subtitle: '多言語対応AIコンシェルジュ',
            to: '/concierge',
            toTop: true,
            icon: 'support_agent',
            iconColor: '#8B5CF6', // brand violet(製品高亮)
          },
        ],
      },
      {
        label: '機能',
        items: [
          { title: 'FAQ・施設案内AI', subtitle: 'よくある質問を自動対応', to: '/solution#feature-faq', icon: 'help' },
          {
            title: 'フロアマップ・ナビゲーション',
            subtitle: '3Dマップで目的地まで案内',
            to: '/solution#feature-floormap',
            icon: 'map',
          },
          {
            title: 'リアルタイム会話',
            subtitle: '10言語以上の音声対話',
            to: '/solution#feature-realtime',
            icon: 'record_voice_over',
          },
        ],
      },
    ],
  },
  {
    title: '料金',
    type: 'link',
    to: '/pricing',
    toTop: true,
  },
  {
    title: '導入事例',
    type: 'link',
    to: '/showcase',
    toTop: true,
  },
  {
    title: 'リソース',
    type: 'dropdown',
    items: [
      { title: 'ブログ', to: '/blog' },
      { title: 'マニュアル', href: 'https://docs.gbase.ai/user-manual/quickstart', target: '_blank' },
      { title: 'リリースノート', href: 'https://cs.gbase.ai/blog/release-notes/' },
    ],
  },
];

interface NavigationProps {
  stack?: boolean;
  onClose?: () => void;
}

/**
 * Navigation组件 - Solution风格
 * 使用纯CSS实现下拉菜单 (group/group-hover)
 *
 * @param stack - 是否为移动端垂直堆叠模式
 * @param onClose - 关闭移动端菜单的回调函数
 */
const Navigation: React.FC<NavigationProps> = ({ stack, onClose }) => {
  const location = useLocation();
  const { pathname } = location;
  const { trackContactClick } = useTracking();

  const isLinkActive = (to?: string) => {
    if (!to) return false;
    if (to === '/') return pathname === '/';
    return pathname.startsWith(to.split('#')[0]);
  };

  const isDropdownActive = (items?: DropdownItem[]) => {
    if (!items) return false;
    return items.some((item) => item.to && pathname.startsWith(item.to.split('#')[0]));
  };

  // Handle special onClick logic for specific navigation items
  const handleNavClick = (item: NavItem | DropdownItem, e?: React.MouseEvent<HTMLAnchorElement>) => {
    // 关闭移动端菜单
    if (onClose) {
      onClose();
    }

    // 滚动到顶部
    if (item.toTop) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    // 同页 hash 滚动：已在目标页面时，拦截导航并手动滚动到对应 section
    if (item.to?.includes('#')) {
      const [path, hash] = item.to.split('#');
      if (path === pathname) {
        e?.preventDefault();
        const element = document.getElementById(hash);
        if (element) {
          const headerHeight = 80;
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({ top: elementPosition - headerHeight, behavior: 'smooth' });
        }
        return;
      }
    }

    // "ブログ" - 新窗口打开
    if (item.title === 'ブログ' && item.to === '/blog') {
      e?.preventDefault();
      window.open('/blog');
    }

    // "お問い合わせ" - tracking (如果存在的话)
    if (item.title === 'お問い合わせ') {
      trackContactClick('header_navigation');
    }
  };

  if (stack) {
    // 移动端版本 (垂直列表)
    return (
      <div className="flex flex-col gap-4">
        {NAV_ITEMS.map((item, index) => {
          if (item.type === 'link') {
            const isActive = isLinkActive(item.to);
            return (
              <SmartLink
                key={index}
                to={item.to}
                href={item.href}
                className={`text-sm font-medium ${isActive ? 'text-violet-500 font-bold' : 'text-[#1A1A2E]'}`}
                onClick={(e) => handleNavClick(item, e)}
              >
                {item.title}
              </SmartLink>
            );
          } else {
            return (
              <div key={index} className="flex flex-col gap-2">
                <div className="text-sm font-medium text-[#1A1A2E]">{item.title}</div>
                <div className="flex flex-col gap-3 pl-4">
                  {item.groups
                    ? item.groups.map((group, gIdx) => (
                        <div key={gIdx} className="flex flex-col gap-2">
                          <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">{group.label}</div>
                          {group.items.map((subItem, subIndex) => (
                            <SmartLink
                              key={subIndex}
                              to={subItem.to}
                              href={subItem.href}
                              target={subItem.target}
                              className="text-sm text-[#555573] hover:text-violet-500 transition-colors"
                              onClick={(e) => handleNavClick(subItem, e)}
                            >
                              <div className="font-medium">{subItem.title}</div>
                              {subItem.subtitle && <div className="text-xs">{subItem.subtitle}</div>}
                            </SmartLink>
                          ))}
                        </div>
                      ))
                    : item.items?.map((subItem, subIndex) => (
                        <SmartLink
                          key={subIndex}
                          to={subItem.to}
                          href={subItem.href}
                          target={subItem.target}
                          className="text-sm text-[#555573] hover:text-violet-500 transition-colors"
                          onClick={(e) => handleNavClick(subItem, e)}
                        >
                          <div className="font-medium">{subItem.title}</div>
                          {subItem.subtitle && <div className="text-xs">{subItem.subtitle}</div>}
                        </SmartLink>
                      ))}
                </div>
              </div>
            );
          }
        })}
      </div>
    );
  }

  // Desktop版本 (水平导航 + CSS hover下拉)
  return (
    <nav className="flex items-center gap-8">
      {NAV_ITEMS.map((item, index) => {
        if (item.type === 'link') {
          const isActive = isLinkActive(item.to);
          return (
            <SmartLink
              key={index}
              to={item.to}
              href={item.href}
              className={`text-sm font-medium py-5 transition-colors ${
                isActive ? 'text-violet-500 font-bold' : 'text-[#555573] hover:text-violet-500'
              }`}
              onClick={(e) => handleNavClick(item, e)}
            >
              {item.title}
            </SmartLink>
          );
        } else {
          const isMultiGroup = !!item.groups;
          return (
            <div key={index} className="relative group">
              <button className="flex items-center gap-1 text-sm font-medium text-[#555573] hover:text-violet-500 py-5 transition-colors">
                {item.title}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div
                className={`absolute left-0 top-full pt-2 hidden group-hover:block z-50 ${isMultiGroup ? '' : 'w-56'}`}
              >
                {isMultiGroup ? (
                  <div className="bg-white border border-[#ECEAF5] rounded-xl shadow-xl p-5 grid grid-cols-2 gap-6 w-[620px]">
                    {item.groups!.map((group, gIdx) => (
                      <div key={gIdx}>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-3">
                          {group.label}
                        </p>
                        {group.items.map((subItem, subIndex) => (
                          <SmartLink
                            key={subIndex}
                            to={subItem.to}
                            href={subItem.href}
                            target={subItem.target}
                            className="block px-3 py-2.5 hover:bg-violet-500/10 hover:text-violet-500 rounded-lg transition-colors text-[#1A1A2E]"
                            onClick={(e) => handleNavClick(subItem, e)}
                          >
                            <div className="flex items-start gap-3">
                              {subItem.icon && <NavIcon name={subItem.icon} color={subItem.iconColor} />}
                              <div>
                                <div className={subItem.subtitle ? 'font-bold text-sm' : 'font-medium text-sm'}>
                                  {subItem.title}
                                </div>
                                {subItem.subtitle && (
                                  <div className="text-xs text-[#8B8AA8] mt-0.5">{subItem.subtitle}</div>
                                )}
                              </div>
                            </div>
                          </SmartLink>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white border border-[#ECEAF5] rounded-xl shadow-xl p-2 flex flex-col gap-1">
                    {item.items?.map((subItem, subIndex) => (
                      <SmartLink
                        key={subIndex}
                        to={subItem.to}
                        href={subItem.href}
                        target={subItem.target}
                        className="block px-4 py-2 text-sm hover:bg-violet-500/10 hover:text-violet-500 rounded transition-colors text-[#1A1A2E]"
                        onClick={(e) => handleNavClick(subItem, e)}
                      >
                        <div className={subItem.subtitle ? 'font-bold' : 'font-medium'}>{subItem.title}</div>
                        {subItem.subtitle && <div className="text-xs text-[#8B8AA8]">{subItem.subtitle}</div>}
                      </SmartLink>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        }
      })}
    </nav>
  );
};

export default Navigation;
